import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { NavBar } from '../components/NavBar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';

export default function PrivacyPage() {
  const { t } = useTranslation();

  return (
    <div className="App">
      <SEOHead
        pageTitleOverride={`${t('privacy.title')} | Legend Spa Hotel`}
        descriptionOverride="Privacy Policy of Legend Spa Hotel GmbH — how we handle your personal data with absolute discretion."
      />
      <NavBar />
      <div className="legal-page" data-testid="privacy-page">
        <div className="legal-hero">
          <div className="container">
            <h1>{t('privacy.title')}</h1>
            <p>{t('privacy.subtitle')}</p>
          </div>
        </div>

        <div className="container">
          <Link to="/" className="legal-back-btn" data-testid="privacy-back-btn">
            <ArrowLeft size={16} /> Back to Home
          </Link>

          <div className="legal-content">
            <h2>1. Data Controller</h2>
            <p>The data controller responsible for the processing of personal data on this website is:</p>
            <p>
              Legend Spa Hotel GmbH<br />
              Project Managers: Anke Dittrich &amp; Volker Suski<br />
              Isla Margarita, Municipio Arismendi, Venezuela<br />
              Email: <a href="mailto:info@legend-spa.me">info@legend-spa.me</a>
            </p>

            <h2>2. What Data We Collect</h2>
            <p>When you use the contact form on our website, we collect the following personal data:</p>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number (optional)</li>
              <li>Subject and message content</li>
              <li>Language preference</li>
              <li>Date and time of submission</li>
            </ul>
            <p>We also collect technical data automatically, including IP address, browser type, and referring pages for security and analytics purposes.</p>

            <h2>3. Purpose of Data Processing</h2>
            <p>We process your personal data for the following purposes:</p>
            <ul>
              <li>To respond to your inquiries submitted through the contact form</li>
              <li>To provide information about Legend Spa Hotel and its services</li>
              <li>To comply with legal obligations</li>
              <li>To protect our legitimate business interests</li>
            </ul>

            <h2>4. Legal Basis</h2>
            <p>The legal basis for processing your data is:</p>
            <ul>
              <li><strong>Consent (GDPR Art. 6(1)(a)):</strong> When you voluntarily submit the contact form</li>
              <li><strong>Legitimate Interest (GDPR Art. 6(1)(f)):</strong> For security and fraud prevention</li>
              <li><strong>Contract Performance (GDPR Art. 6(1)(b)):</strong> When processing is necessary for a contract</li>
            </ul>

            <h2>5. Data Retention</h2>
            <p>We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, or as required by applicable law. Contact form submissions are typically retained for 24 months.</p>

            <h2>6. Data Sharing</h2>
            <p>We do not sell, trade, or rent your personal data to third parties. We may share data with trusted service providers who assist in operating our website and conducting our business, subject to confidentiality agreements. These include:</p>
            <ul>
              <li>Email service providers (IONOS / 1&1)</li>
              <li>Security services (Cloudflare)</li>
              <li>Database hosting providers</li>
            </ul>

            <h2>7. Cloudflare Turnstile</h2>
            <p>We use Cloudflare Turnstile for bot protection on our contact form. Cloudflare may process certain data to perform its bot detection service. Please refer to Cloudflare's privacy policy at <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">cloudflare.com/privacypolicy</a>.</p>

            <h2>8. Your Rights</h2>
            <p>Under the GDPR and applicable data protection laws, you have the following rights:</p>
            <ul>
              <li><strong>Right of access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Right to rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to restriction:</strong> Request limited processing of your data</li>
              <li><strong>Right to data portability:</strong> Receive your data in a portable format</li>
              <li><strong>Right to object:</strong> Object to processing based on legitimate interest</li>
              <li><strong>Right to withdraw consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p>To exercise any of these rights, please contact us at <a href="mailto:info@legend-spa.me">info@legend-spa.me</a>.</p>

            <h2>9. Security</h2>
            <p>We implement appropriate technical and organisational security measures to protect your personal data against accidental or unlawful destruction, loss, alteration, unauthorised disclosure, or access. Our email communications are transmitted via SSL/TLS encryption.</p>

            <h2>10. Cookies</h2>
            <p>This website uses only essential technical cookies necessary for its operation. We use localStorage to remember your language preference. We do not use tracking cookies or advertising cookies.</p>

            <h2>11. Changes to This Policy</h2>
            <p>We reserve the right to update this Privacy Policy at any time. Changes will be effective immediately upon posting. We encourage you to review this page periodically.</p>

            <h2>12. Contact</h2>
            <p>For any questions regarding this Privacy Policy or our data processing practices, please contact:</p>
            <p>
              Legend Spa Hotel GmbH<br />
              Email: <a href="mailto:info@legend-spa.me">info@legend-spa.me</a>
            </p>

            <p style={{ marginTop: 40, color: 'var(--color-text-dim)', fontSize: '0.82rem' }}>
              Last updated: January 2026
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
